import { createAsyncThunk, miniSerializeError } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { RootState } from 'src/store';
import { IEntry, IProof } from 'src/ts/interfaces';
import api from './api';
import { NAME } from './constants';

const serializeWithStatus = (error: unknown) => ({
  ...miniSerializeError(error),
  code: String((error as AxiosError)?.response?.status ?? '')
});

export const getEntriesByFolderIdThunk = createAsyncThunk(
  `${NAME}/getEntriesByFolderId`,
  async (folderId: string) => {
    try {
      return api.getEntriesByFolderId(folderId);
    } catch (error) {
      console.error(`${NAME}/getEntriesByFolderId`, error);
      throw new Error('Failed to get entries');
    }
  }
);

export const getEntryByIdThunk = createAsyncThunk(
  `${NAME}/getEntryById`,
  async (entryId: string) => {
    try {
      return api.getEntryById(entryId);
    } catch (error) {
      console.error(`${NAME}/getEntryById`, error);
      throw new Error('Failed to get entry');
    }
  },
  {
    serializeError: serializeWithStatus,
    condition: (entryId, { getState }) => !(getState() as RootState)[NAME].fetchingIds[entryId]
  }
);

export const updateEntryThunk = createAsyncThunk(
  `${NAME}/updateEntry`,
  async ({ id, ...entry }: { id: string } & Partial<IEntry>) => {
    try {
      return api.updateEntry(id, entry);
    } catch (error) {
      console.error(`${NAME}/updateEntry`, error);
      throw new Error('Failed to update entry');
    }
  }
);

export const splitEntryThunk = createAsyncThunk(`${NAME}/splitEntry`, async (entryId: string) => {
  try {
    return api.splitEntry(entryId);
  } catch (error) {
    console.error(`${NAME}/splitEntry`, error);
    throw new Error('Failed to split entry');
  }
});

export const mergeEntryThunk = createAsyncThunk(
  `${NAME}/mergeEntry`,
  async ({ matchingEntryId, entryId }: { entryId: string; matchingEntryId: string }) => {
    try {
      return api.mergeEntry(matchingEntryId, entryId);
    } catch (error) {
      console.error(`${NAME}/mergeEntry`, error);
      throw new Error('Failed to merge entry');
    }
  }
);

export const sendReportThunk = createAsyncThunk(
  `${NAME}/sendReport`,
  async ({ entryId, proofs }: { entryId: string; proofs?: IProof[] }, { rejectWithValue }) => {
    try {
      return proofs ? await api.sendReport(entryId, proofs) : await api.sendReport(entryId);
    } catch (error: any) {
      console.error(`${NAME}/sendReport`, error);
      // ITL-2816: preserve the backend error body (e.g. the 422
      // `pending_proofs_unresolved` shape) so useSendReport can branch on the
      // code instead of swallowing it into a generic Error.
      return rejectWithValue(error?.response?.data ?? { message: 'Failed to send report' });
    }
  }
);

export const requestDocumentsThunk = createAsyncThunk(
  `${NAME}/requestDocuments`,
  async (
    { id, message, proofs }: { id: string; message: string; proofs?: IProof[] },
    { rejectWithValue }
  ) => {
    try {
      return await api.requestDocuments(id, message, proofs);
    } catch (error: any) {
      console.error(`${NAME}/requestDocuments`, error);
      // ITL-2816: preserve the backend error body (e.g. the 422
      // `pending_proofs_unresolved` shape) so useRequestDocuments can branch on
      // the code instead of swallowing it into a generic Error. Mirrors sendReportThunk.
      return rejectWithValue(error?.response?.data ?? { message: 'Failed to request documents' });
    }
  }
);
